import { useState, useEffect } from 'react';
import DraggableFieldList from './DraggableFieldList';
import LogoPicker from './LogoPicker';
import { TemplateData, FieldConfig } from '../types/template';
import { generateTemplateHTML } from '../services/templateHtmlGenerator';

interface TemplateBuilderProps {
  onGenerate: (html: string) => void;
  initialData?: TemplateData | null;
  onDataChange?: (data: TemplateData) => void;
  forceShowGrid?: boolean; // Force showGrid to specific value (for saving)
  onShowGridChange?: (showGrid: boolean) => void; // Notify parent of showGrid state
  demoMode?: boolean; // Demo mode: disable logo picker and uploads
}

const defaultFields: FieldConfig[] = [
  { id: 'logo', label: 'Company Logo', variable: 'logo', enabled: true, row: 0, col: 1 },
  { id: 'secondaryLogo', label: 'Secondary Logo', variable: 'secondaryLogo', enabled: false },
  { id: 'profilePicture', label: 'Profile Picture', variable: 'profilePicture', enabled: false },
  { id: 'name', label: 'Full Name (Display)', variable: 'displayName', enabled: true, row: 0, col: 0 },
  { id: 'fullName', label: 'Full Name (First + Last)', variable: 'fullName', enabled: false },
  { id: 'firstName', label: 'First Name', variable: 'givenName', enabled: false, conditional: true },
  { id: 'lastName', label: 'Last Name', variable: 'surname', enabled: false, conditional: true },
  { id: 'jobTitle', label: 'Job Title', variable: 'jobTitle', enabled: true, conditional: true, row: 1, col: 0 },
  { id: 'department', label: 'Department', variable: 'department', enabled: true, conditional: true, row: 2, col: 0 },
  { id: 'officeLocation', label: 'Office Location', variable: 'officeLocation', enabled: false, conditional: true },
  { id: 'streetAddress', label: 'Street Address', variable: 'streetAddress', enabled: false, conditional: true },
  { id: 'city', label: 'City', variable: 'city', enabled: false, conditional: true },
  { id: 'state', label: 'State/Province', variable: 'state', enabled: false, conditional: true },
  { id: 'postalCode', label: 'Postal Code', variable: 'postalCode', enabled: false, conditional: true },
  { id: 'country', label: 'Country', variable: 'country', enabled: false, conditional: true },
  { id: 'companyName', label: 'Company Name (from M365)', variable: 'companyName', enabled: false, conditional: true },
  { id: 'company', label: 'Company (Template)', variable: 'companyName', enabled: true, row: 3, col: 0 },
  { id: 'website', label: 'Website', variable: 'website', enabled: true, row: 4, col: 0 },
  { id: 'email', label: 'Email', variable: 'email', enabled: true, prefix: 'E:', row: 5, col: 0 },
  { id: 'phone', label: 'Business Phone', variable: 'businessPhones', enabled: false, conditional: true, prefix: 'T:' },
  { id: 'mobile', label: 'Mobile Phone', variable: 'mobilePhone', enabled: true, conditional: true, prefix: 'M:', row: 6, col: 0 },
  { id: 'faxNumber', label: 'Fax Number', variable: 'faxNumber', enabled: false, conditional: true, prefix: 'F:' },
  { id: 'employeeId', label: 'Employee ID', variable: 'employeeId', enabled: false, conditional: true },
  { id: 'employeeType', label: 'Employee Type', variable: 'employeeType', enabled: false, conditional: true },
  { id: 'employeeHireDate', label: 'Hire Date', variable: 'employeeHireDate', enabled: false, conditional: true },
  { id: 'preferredLanguage', label: 'Preferred Language', variable: 'preferredLanguage', enabled: false, conditional: true },
  { id: 'usageLocation', label: 'Usage Location', variable: 'usageLocation', enabled: false, conditional: true },
  { id: 'extensionAttribute1', label: 'Extension Attribute 1', variable: 'extensionAttribute1', enabled: false, conditional: true },
  { id: 'extensionAttribute2', label: 'Extension Attribute 2', variable: 'extensionAttribute2', enabled: false, conditional: true },
  { id: 'extensionAttribute3', label: 'Extension Attribute 3', variable: 'extensionAttribute3', enabled: false, conditional: true },
  { id: 'extensionAttribute4', label: 'Extension Attribute 4', variable: 'extensionAttribute4', enabled: false, conditional: true },
  { id: 'extensionAttribute5', label: 'Extension Attribute 5', variable: 'extensionAttribute5', enabled: false, conditional: true },
  { id: 'extensionAttribute6', label: 'Extension Attribute 6', variable: 'extensionAttribute6', enabled: false, conditional: true },
  { id: 'extensionAttribute7', label: 'Extension Attribute 7', variable: 'extensionAttribute7', enabled: false, conditional: true },
  { id: 'extensionAttribute8', label: 'Extension Attribute 8', variable: 'extensionAttribute8', enabled: false, conditional: true },
  { id: 'extensionAttribute9', label: 'Extension Attribute 9', variable: 'extensionAttribute9', enabled: false, conditional: true },
  { id: 'extensionAttribute10', label: 'Extension Attribute 10', variable: 'extensionAttribute10', enabled: false, conditional: true },
  { id: 'extensionAttribute11', label: 'Extension Attribute 11', variable: 'extensionAttribute11', enabled: false, conditional: true },
  { id: 'extensionAttribute12', label: 'Extension Attribute 12', variable: 'extensionAttribute12', enabled: false, conditional: true },
  { id: 'extensionAttribute13', label: 'Extension Attribute 13', variable: 'extensionAttribute13', enabled: false, conditional: true },
  { id: 'extensionAttribute14', label: 'Extension Attribute 14', variable: 'extensionAttribute14', enabled: false, conditional: true },
  { id: 'extensionAttribute15', label: 'Extension Attribute 15', variable: 'extensionAttribute15', enabled: false, conditional: true },
  { id: 'vat', label: 'VAT Number (Template)', variable: 'vatNumber', enabled: false, prefix: 'VAT:' },
  { id: 'address', label: 'Address (Template)', variable: 'address', enabled: false },
  // Shared Mailbox fields (available in hybrid mode when composing from a shared mailbox)
  { id: 'sharedDisplayName', label: 'Shared: Display Name', variable: 'shared.displayName', enabled: false, conditional: true },
  { id: 'sharedEmail', label: 'Shared: Email', variable: 'shared.email', enabled: false, conditional: true, prefix: 'E:' },
  { id: 'sharedJobTitle', label: 'Shared: Job Title', variable: 'shared.jobTitle', enabled: false, conditional: true },
  { id: 'sharedDepartment', label: 'Shared: Department', variable: 'shared.department', enabled: false, conditional: true },
  { id: 'sharedMobilePhone', label: 'Shared: Mobile Phone', variable: 'shared.mobilePhone', enabled: false, conditional: true, prefix: 'M:' },
  { id: 'sharedBusinessPhone', label: 'Shared: Business Phone', variable: 'shared.businessPhone', enabled: false, conditional: true, prefix: 'T:' },
  { id: 'sharedOfficeLocation', label: 'Shared: Office Location', variable: 'shared.officeLocation', enabled: false, conditional: true },
  { id: 'sharedCompanyName', label: 'Shared: Company Name', variable: 'shared.companyName', enabled: false, conditional: true },
];

const defaultData: TemplateData = {
  layout: 'horizontal',
  layoutMode: 'list',
  gridRows: 7,
  gridCols: 2,
  logoUrl: '',
  logoWidth: '100',
  logoPosition: 'left',
  logoAlignToField: 'name',
  secondaryLogoUrl: '',
  secondaryLogoWidth: '80',
  secondaryLogoPosition: 'bottom',
  showSecondaryLogo: false,
  showJobTitle: true,
  showDepartment: true,
  showPhone: false,
  showMobile: true,
  showAddress: false,
  companyName: '{{companyName}}',
  website: 'https://{{companyDomain}}',
  vatNumber: '',
  addressLine1: '',
  addressLine2: '',
  socialLinks: {
    linkedin: '',
    twitter: '',
    facebook: '',
  },
  fontFamily: 'Arial, sans-serif',
  fontSize: '14',
  primaryColor: '#000000',
  showSocialIcons: false,
  fields: defaultFields,
  customTexts: {},
};

export default function TemplateBuilder({ onGenerate, initialData, onDataChange, forceShowGrid, onShowGridChange, demoMode }: TemplateBuilderProps) {
  // Merge initialData with defaultData, ensuring all default fields are present
  const initializeData = (): TemplateData => {
    if (!initialData) return defaultData;
    
    if (!initialData.fields) {
      // No fields in saved data, use defaults
      return { ...defaultData, ...initialData, fields: defaultFields };
    }
    
    // Start with saved fields to preserve order and dynamic fields (spacers, custom text)
    const mergedFields = [...initialData.fields];
    
    // Add any new default fields that don't exist in saved fields
    defaultFields.forEach(defaultField => {
      if (!mergedFields.find(f => f.id === defaultField.id)) {
        mergedFields.push(defaultField);
      }
    });
    
    return {
      ...defaultData,
      ...initialData,
      fields: mergedFields,
    };
  };
  
  const [data, setData] = useState<TemplateData>(initializeData());
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [showSecondaryLogoPicker, setShowSecondaryLogoPicker] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  // Use forceShowGrid if provided, otherwise use internal state
  const effectiveShowGrid = forceShowGrid !== undefined ? forceShowGrid : showGrid;

  // Auto-update preview when data changes
  useEffect(() => {
    // Ensure all enabled fields have row/col positions for grid layout
    let enabledFieldIndex = 0;
    const fieldsWithPositions = data.fields?.map((field) => {
      if (field.enabled && (field.row === undefined || field.col === undefined)) {
        // Auto-assign missing positions based on enabled field count
        const autoRow = Math.floor(enabledFieldIndex / 2);
        const autoCol = enabledFieldIndex % 2;
        enabledFieldIndex++;
        return { ...field, row: autoRow, col: autoCol };
      } else if (field.enabled) {
        enabledFieldIndex++;
      }
      return field;
    }) || defaultFields;
    
    const updatedData = { ...data, fields: fieldsWithPositions };
    
    // Debug: Log grid positioning
    console.log('Template Builder - Grid positioning:', fieldsWithPositions.filter(f => f.enabled).map(f => ({ id: f.id, row: f.row, col: f.col })));
    
    const html = generateTemplateHTML(updatedData, defaultFields, effectiveShowGrid);
    console.log('Template Builder - Generated HTML preview:', html.substring(0, 200) + '...');
    
    onGenerate(html);
    if (onDataChange) onDataChange(updatedData);
  }, [data, effectiveShowGrid]);

  // Notify parent of showGrid changes
  useEffect(() => {
    if (onShowGridChange) {
      onShowGridChange(showGrid);
    }
  }, [showGrid]);

  const handleGenerate = () => {
    const html = generateTemplateHTML(data, defaultFields, showGrid);
    onGenerate(html);
  };

  const handleFileUpload = async (file: File) => {
    setUploadError('');
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file (PNG, JPG, GIF)');
      return;
    }
    
    // Validate file size (max 500KB)
    const maxSize = 500 * 1024; // 500KB
    if (file.size > maxSize) {
      setUploadError(`File too large (${Math.round(file.size / 1024)}KB). Maximum is 500KB`);
      return;
    }
    
    try {
      // Upload file to API
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/upload/logo', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      const result = await response.json();
      
      if (!result.success) {
        setUploadError(result.error || 'Upload failed');
        return;
      }
      
      // Use the returned file URL
      setData({ ...data, logoUrl: result.data.url });
    } catch (error) {
      setUploadError('Failed to upload file');
      console.error('Upload error:', error);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const previewHTML = () => {
    // Use the same data processing as the main preview to ensure consistency
    let enabledFieldIndex = 0;
    const fieldsWithPositions = data.fields?.map((field) => {
      if (field.enabled && (field.row === undefined || field.col === undefined)) {
        const autoRow = Math.floor(enabledFieldIndex / 2);
        const autoCol = enabledFieldIndex % 2;
        enabledFieldIndex++;
        return { ...field, row: autoRow, col: autoCol };
      } else if (field.enabled) {
        enabledFieldIndex++;
      }
      return field;
    }) || defaultFields;
    
    const updatedData = { ...data, fields: fieldsWithPositions, layoutMode: 'grid' };
    return generateTemplateHTML(updatedData, defaultFields, showGrid)
      .replace(/\{\{displayName\}\}/g, 'John Doe')
      .replace(/\{\{jobTitle\}\}/g, 'Senior Developer')
      .replace(/\{\{department\}\}/g, 'Engineering')
      .replace(/\{\{email\}\}/g, 'john.doe@company.com')
      .replace(/\{\{mobilePhone\}\}/g, '+45 12 34 56 78')
      .replace(/\{\{businessPhones\}\}/g, '+45 87 65 43 21')
      .replace(/\{\{companyName\}\}/g, 'Acme Corp')
      .replace(/\{\{companyDomain\}\}/g, 'acme.com')
      .replace(/\{\{#if \w+\}\}/g, '')
      .replace(/\{\{\/if\}\}/g, '');
  };

  return (
    <div>
      {/* TOP SECTION: Template Settings */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '600' }}>Template Settings</h4>
      <div id="signature-fields-section" style={{ marginTop: '30px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
        <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>Signature Fields</h4>
        
        {/* Layout Mode Toggle, Font Size and Text Color */}
        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: '500', fontSize: '13px', whiteSpace: 'nowrap' }}>Font Size:</label>
          <input
            type="number"
            value={data.fontSize}
            onChange={(e) => setData({ ...data, fontSize: e.target.value })}
            style={{ width: '70px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          />
          <span style={{ fontSize: '13px', color: '#6b7280' }}>px</span>
          
          <div style={{ width: '1px', height: '30px', backgroundColor: '#d1d5db', margin: '0 10px' }} />
          
          <label style={{ fontWeight: '500', fontSize: '13px', whiteSpace: 'nowrap' }}>Text Color:</label>
          <input
            type="color"
            value={data.primaryColor}
            onChange={(e) => setData({ ...data, primaryColor: e.target.value })}
            style={{ width: '50px', height: '32px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
          />
          
          <div style={{ width: '1px', height: '30px', backgroundColor: '#d1d5db', margin: '0 10px' }} />
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500', fontSize: '13px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            📐 Show Grid
          </label>
          <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '4px 8px', borderRadius: '4px' }}>{data.primaryColor}</code>
        </div>
        
        {/* Field List */}
        <DraggableFieldList
          fields={data.fields || defaultFields}
          onChange={(newFields) => setData({ ...data, fields: newFields })}
          demoMode={demoMode}
        />
      </div>


      </div>

      {/* Logo Picker Modals */}
      {showLogoPicker && (
        <LogoPicker
          currentUrl={data.logoUrl}
          onSelect={(url) => {
            setData({ ...data, logoUrl: url });
            setShowLogoPicker(false);
          }}
          onClose={() => setShowLogoPicker(false)}
        />
      )}

      {showSecondaryLogoPicker && (
        <LogoPicker
          currentUrl={data.secondaryLogoUrl}
          onSelect={(url) => {
            setData({ ...data, secondaryLogoUrl: url });
            setShowSecondaryLogoPicker(false);
          }}
          onClose={() => setShowSecondaryLogoPicker(false)}
        />
      )}
    </div>
  );
}
